import { useState, FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import { setToken, setUser, isAuthenticated } from '@/lib/auth'
import logoImage from '@/assets/logo.png'
import { ApiError } from '@/types'
import type { User } from '@/types'

function decodeBase64Url(input: string): string {
  let sanitized = input.replace(/-/g, '+').replace(/_/g, '/')
  const padding = sanitized.length % 4
  if (padding) {
    sanitized += '='.repeat(4 - padding)
  }
  return atob(sanitized)
}

function parseJwtPayload(token: string) {
  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid token format')
  }
  const payload = decodeBase64Url(parts[1])
  return JSON.parse(payload)
}

export function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  if (isAuthenticated()) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await api.login(username, password)

      setToken(result.token)

      let user = result.user
      if (!user) {
        const payload = parseJwtPayload(result.token)
        user = payload['user'] as User
      }

      setUser(user as User)
      
      navigate('/', { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      const genericMessage = '登录失败，请稍后重试'
      if (apiError?.message) {
        setError(apiError.message)
      } else if (err instanceof Error && err.message) {
        setError(genericMessage)
        console.error('JWT decode error:', err)
      } else {
        setError(genericMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img
            src={logoImage}
            alt="慕途数字图书馆标志"
            className="mx-auto mb-3 h-16 w-16 rounded-xl shadow-sm"
          />
          <CardTitle className="text-2xl">慕途</CardTitle>
          <CardDescription>管理员登录</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="例如：admin"
            />
            
            <Input
              label="密码"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="输入密码"
            />

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={loading}
            >
              登录
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
